import React, { useEffect, useState } from "react";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "../../../firebase";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../design/fieldset";
import { getSubdomainFromUrl } from "../../../utils/url";
import {
  TeamMember,
  TeamMemberRole,
  teamMemberRoles,
} from "../../../models/TeamMember";
import { useTeamMember } from "../../../contexts/TeamMemberContext";
import { Select } from "../../../design/select";

interface Props {
  teamMember: TeamMember;
}

interface TeamMemberUpdateData {
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
}

interface Errors {
  firstName?: string;
  lastName?: string;
}

const EditTeamMemberButton: React.FC<Props> = ({ teamMember }) => {
  const { updateTeamMember } = useTeamMember();
  const [role, setRole] = useState(teamMember?.role ?? "admin");
  const [photo, setPhoto] = useState<File | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    if (!teamMember) return;

    setIsSaving(true);

    try {
      await updateTeamMember(teamMember._id, {
        role,
      });
      setIsOpen(false);
    } catch (error) {
      console.error("Error updating teamMember:", error);
    }

    setIsSaving(false);
  };

  const clearForm = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Edit
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Edit Team Member</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Role</Label>
                <Select
                  value={role}
                  onChange={(e) => setRole(e.target.value as TeamMemberRole)}
                >
                  {teamMemberRoles.map((role) => (
                    <option key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </option>
                  ))}
                </Select>
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditTeamMemberButton;
